import Layout from '../../components/Layout';
import MenuBar from '@/components/MenuBar';
import { Content } from './styles';
import { lazy, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Resume from './Resume';
import Breadcrumb from '@/components/Breadcrumb';
import General from './General';
import Security from './Security';
import Updates from './Updates';
import Hosts from './Hosts';
import Stack from '@mui/material/Stack';
import Wireless from './Wireless';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { useEquipment } from '@/hooks/useEquipment';
import type { IconName } from 'lucide-react/dynamic';
import Tree from './Tree';
import { EquipmentProvider } from '@/context/EquipmentContext';
import Management from './Management';
import Customer from './Customer';
import Fallback from '@/components/Fallback';
import Tools from './Tools';
import VoIP from './VoIP';

const Latency = lazy(() => import('@/pages/Tools/Latency'));

function Equipment() {
  const { id } = useParams<'id'>();
  const equipmentId = id ?? 'unknown';
  const { t } = useTranslation();
  const theme = useTheme();

  /** Hook global de equipamento */
  const equipmentHookValues = useEquipment(id);
  const { equipment, params } = equipmentHookValues;

  /** View states */
  const [currentTab, setCurrentTab] = useState('general');
  const [modeView, setModeView] = useState('grid');
  const isLg = useMediaQuery(theme.breakpoints.down(1400));

  // sets the resolution mode view based on the screen size
  useEffect(() => {
    setModeView(isLg ? 'list' : 'grid');
  }, [isLg]);

  // Media query
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  if (!equipment) {
    return (
      <Layout>
        <Fallback
          text={t('equipment.resume.loadingEquipment')}
          variant="compact"
        />
      </Layout>
    );
  }

  interface LinkItem {
    path: string;
    icon: IconName;
    label: string;
    action: () => void;
  }

  const links: LinkItem[] = [
    {
      action: () => setCurrentTab('general'),
      path: 'general',
      icon: 'presentation',
      label: 'Visão Geral',
    },
    {
      action: () => setCurrentTab('wireless'),
      path: 'wireless',
      icon: 'wifi',
      label: 'Wireless',
    },
    {
      path: 'connected-devices',
      action: () => setCurrentTab('connected-devices'),
      icon: 'pc-case',
      label: t('equipment.connectedHosts.title'),
    },
    {
      path: 'customer',
      action: () => setCurrentTab('customer'),
      icon: 'user',
      label: t('equipment.customer.title'),
    },
    {
      path: 'diagnostics',
      action: () => setCurrentTab('diagnostics'),
      icon: 'stethoscope',
      label: t('equipment.tools.title'),
    },
    {
      path: 'management',
      action: () => setCurrentTab('management'),
      icon: 'cloud-cog',
      label: t('equipment.management.tab'),
    },
    {
      path: 'updates',
      action: () => setCurrentTab('updates'),
      icon: 'refresh-cw',
      label: t('equipment.updates.tab'),
    },
    {
      path: 'tree',
      action: () => setCurrentTab('tree'),
      icon: 'list-tree',
      label: t('equipment.tree.title'),
    },
    ...(equipment.model?.ont
      ? [
          {
            path: 'voip',
            action: () => setCurrentTab('voip'),
            icon: 'phone-call' as IconName,
            label: 'VoIP',
          },
        ]
      : []),
  ];

  return (
    <EquipmentProvider values={equipmentHookValues}>
      <Layout noLeftMargin>
        <Stack
          spacing={1}
          width={{ xs: '100%', md: '90%' }}
          alignItems="center"
          mx="auto"
        >
          {/* Breadcrumb */}
          <Stack width="100%" paddingLeft={isMd || isXs ? '16px' : '24px'}>
            <Breadcrumb
              paths={[
                { label: t('breadcrumbs.dashboard'), route: '/' },
                { label: t('breadcrumbs.list'), route: '/list' },
                {
                  label: `${t('breadcrumbs.equipment')} ${equipment.serialNumber}`,
                  route: `/equipment/${equipmentId}`,
                },
              ]}
            />
          </Stack>

          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <Content>
              <Resume modeView={modeView} setModeView={setModeView} />

              <Stack>
                <MenuBar links={links} currentPath={currentTab} />
              </Stack>

              {currentTab === 'general' && <General modeView={modeView} />}

              {currentTab === 'wireless' && <Wireless modeView={modeView} />}

              {currentTab === 'latency' && <Latency />}

              {currentTab === 'security' && <Security />}

              {currentTab === 'updates' && <Updates equipment={equipment} />}

              {currentTab === 'connected-devices' && (
                <Hosts currentTab={currentTab} equipmentId={equipment.id} />
              )}

              {currentTab === 'management' && (
                <Management modeView={modeView} />
              )}

              {currentTab === 'customer' && (
                <Customer
                  modeView={modeView}
                  customerData={equipment.customerData}
                />
              )}

              {currentTab === 'tree' && (
                <Tree params={params} deviceId={equipment.id} />
              )}

              {currentTab === 'voip' && <VoIP modeView={modeView} />}
              {currentTab === 'diagnostics' && <Tools />}
            </Content>
          </div>
        </Stack>
      </Layout>
    </EquipmentProvider>
  );
}

export default Equipment;
